/**
 * Body Controller - Manages overlay state, URL hashes, and inert attribute
 *
 * Features:
 * - Sets `inert` attribute on body-controller when overlays are open
 * - Updates URL hash when overlays open/close (e.g., #filters, #cart)
 * - Handles browser back/forward navigation
 * - Portals overlay elements to #OverlayContainer on page load
 */

if (!customElements.get('body-controller')) {
  customElements.define(
    'body-controller',
    class BodyController extends HTMLElement {
      constructor() {
        super();
        this.activeOverlays = new Set();
        this.savedScrollPosition = 0;
        this.hashMap = {
          filters: 'ResultsFiltersDrawer',
          cart: 'CartDrawerElement',
          login: 'LoginOverlay',
          newsletter: 'NewsletterOverlay',
        };
      }

      connectedCallback() {
        this.bindEvents();
        this.portalOverlays();
        // Defer hash check to allow other components to initialize
        requestAnimationFrame(() => {
          this.checkHashOnLoad();
        });
      }

      bindEvents() {
        document.addEventListener('overlay:open', this.handleOverlayOpen.bind(this));
        document.addEventListener('overlay:close', this.handleOverlayClose.bind(this));
        window.addEventListener('hashchange', this.handleHashChange.bind(this));
        document.addEventListener('keyup', this.handleKeyUp.bind(this));

        // Focus cycling for overlays (inert blocks focus but doesn't cycle it)
        const overlayContainer = document.getElementById('OverlayContainer');
        if (overlayContainer) {
          overlayContainer.addEventListener('keydown', this.handleOverlayKeydown.bind(this));
        }
      }

      handleOverlayKeydown(event) {
        if (event.code?.toUpperCase() !== 'TAB') return;
        if (this.activeOverlays.size === 0) return;

        const overlayContainer = document.getElementById('OverlayContainer');
        const focusableSelector =
          'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:not([type=hidden]):enabled, select:enabled, textarea:enabled';
        const focusableElements = Array.from(overlayContainer.querySelectorAll(focusableSelector)).filter(
          (el) => el.offsetParent !== null && !el.closest('[inert]')
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        // Tab on last element -> cycle to first
        if (event.target === lastElement && !event.shiftKey) {
          event.preventDefault();
          firstElement.focus();
        }

        // Shift+Tab on first element -> cycle to last
        if (event.target === firstElement && event.shiftKey) {
          event.preventDefault();
          lastElement.focus();
        }
      }

      handleKeyUp(event) {
        if (event.code?.toUpperCase() !== 'ESCAPE') return;
        if (this.activeOverlays.size > 0) {
          // Close all overlays on escape
          document.dispatchEvent(new CustomEvent('overlay:request-close-all'));
        }
      }

      /**
       * Moves elements with data-overlay-portal attribute to #OverlayContainer
       */
      portalOverlays() {
        const overlayContainer = document.getElementById('OverlayContainer');
        if (!overlayContainer) return;

        const portals = document.querySelectorAll('[data-overlay-portal]');
        portals.forEach((element) => {
          // Clone to preserve event listeners if needed, or just move
          overlayContainer.appendChild(element);
        });
      }

      /**
       * Handle overlay open event
       * @param {CustomEvent} event - Contains { hash, overlayId }
       */
      handleOverlayOpen(event) {
        const { hash, overlayId } = event.detail || {};

        if (overlayId) {
          this.activeOverlays.add(overlayId);
        }

        // Set inert attribute when any overlay is open
        this.setAttribute('inert', '');

        // Set inert on all OTHER overlays in the container
        this.updateOverlayInertState(overlayId);

        // Lock scroll position - save current position and fix body in place
        if (this.activeOverlays.size === 1) {
          this.savedScrollPosition = window.scrollY;
          document.body.style.position = 'fixed';
          document.body.style.top = `-${this.savedScrollPosition}px`;
          document.body.style.left = '0';
          document.body.style.right = '0';
          document.body.style.overflow = 'hidden';
        }

        // Update URL hash
        if (hash && window.location.hash !== `#${hash}`) {
          history.pushState({ overlay: hash }, '', `${window.location.pathname}${window.location.search}#${hash}`);
        }
      }

      /**
       * Handle overlay close event
       * @param {CustomEvent} event - Contains { overlayId }
       */
      handleOverlayClose(event) {
        const { overlayId } = event.detail || {};

        if (overlayId) {
          this.activeOverlays.delete(overlayId);
        }

        // Only remove inert if no overlays are open
        if (this.activeOverlays.size === 0) {
          this.removeAttribute('inert');

          // Remove inert from all overlays
          this.updateOverlayInertState(null);

          // Restore body position and scroll
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.left = '';
          document.body.style.right = '';
          document.body.style.overflow = '';
          window.scrollTo(0, this.savedScrollPosition);

          // Remove hash from URL
          if (window.location.hash) {
            history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        }
      }

      /**
       * Update inert state on overlay elements
       * @param {string|null} activeOverlayId - ID of the active overlay, or null if none
       */
      updateOverlayInertState(activeOverlayId) {
        const overlayContainer = document.getElementById('OverlayContainer');
        if (!overlayContainer) return;

        // Get all direct children of overlay container (the overlay elements)
        const overlays = overlayContainer.children;

        for (const overlay of overlays) {
          if (activeOverlayId && overlay.id !== activeOverlayId) {
            // Another overlay is active - make this one inert
            overlay.setAttribute('inert', '');
          } else {
            // No overlay active or this is the active one - remove inert
            overlay.removeAttribute('inert');
          }
        }
      }

      /**
       * Find overlay ID for a given hash
       * First checks hardcoded hashMap, then looks for [data-overlay-hash] elements
       */
      getOverlayIdForHash(hash) {
        if (!hash) return null;

        // Check hardcoded map first
        if (this.hashMap[hash]) {
          return this.hashMap[hash];
        }

        // Fall back to auto-discovery via data attribute
        const element = document.querySelector(`[data-overlay-hash="${hash}"]`);
        return element ? element.id : null;
      }

      /**
       * Handle browser back/forward navigation
       */
      handleHashChange() {
        const hash = window.location.hash.slice(1); // Remove #
        const overlayId = this.getOverlayIdForHash(hash);

        if (overlayId) {
          // Hash present - dispatch event to open overlay
          document.dispatchEvent(
            new CustomEvent('overlay:request-open', {
              detail: { hash, overlayId },
            })
          );
        } else {
          // No hash or unknown hash - close all overlays
          document.dispatchEvent(new CustomEvent('overlay:request-close-all'));
        }
      }

      /**
       * Check URL hash on page load and open corresponding overlay
       */
      checkHashOnLoad() {
        const hash = window.location.hash.slice(1);
        const overlayId = this.getOverlayIdForHash(hash);

        if (overlayId) {
          document.dispatchEvent(
            new CustomEvent('overlay:request-open', {
              detail: { hash, overlayId },
            })
          );
        }
      }
    }
  );
}
